<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div class="border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">{{ title }}</h3>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Lista somente leitura. A mudança é feita
                {{
                    isProducer
                        ? "no cadastro do produtor"
                        : "pela consulta por código, acima"
                }}.
            </p>
        </div>

        <form class="flex flex-wrap items-end gap-3 px-5 py-3" @submit.prevent="apply">
            <div class="w-52">
                <single-select
                    v-model="filters.value"
                    label="Valor"
                    :options="valueOptions"
                />
            </div>
            <div class="grow">
                <text-input
                    v-model="filters.search"
                    label="Busca"
                    :placeholder="searchPlaceholder"
                />
            </div>
            <button
                type="submit"
                class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                :disabled="loading"
            >
                Filtrar
            </button>
        </form>

        <div v-if="loading" class="px-5 py-6 text-sm text-gray-500 dark:text-gray-400">
            Buscando registros...
        </div>

        <empty-state
            v-else-if="rows.length === 0"
            :title="emptyTitle"
            :message="emptyMessage"
        />

        <div v-else class="overflow-x-auto">
            <table class="w-full table-auto border-collapse text-sm">
                <thead>
                    <tr class="border-b border-black/5 dark:border-white/10">
                        <th
                            v-for="header in headers"
                            :key="header"
                            scope="col"
                            class="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
                        >
                            {{ header }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="isProducer ? row.user_id : row.offer_id"
                        class="border-b border-black/5 dark:border-white/[0.06]"
                    >
                        <template v-if="isProducer">
                            <td class="table-cell-sm">
                                <p class="text-gray-900 dark:text-white">{{ row.name || "—" }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ row.email || "—" }}
                                </p>
                            </td>
                            <td class="table-cell-sm">
                                <span
                                    v-if="row.deleted"
                                    class="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200"
                                >
                                    Excluído
                                </span>
                                <span v-else class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ row.status || "—" }}
                                </span>
                            </td>
                        </template>
                        <template v-else>
                            <td class="table-cell-sm">
                                <p class="font-mono text-gray-900 dark:text-white">
                                    {{ row.code }}
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ row.name || "—" }}
                                </p>
                            </td>
                            <td class="table-cell-sm text-gray-900 dark:text-white">
                                {{ row.product_name || "—" }}
                            </td>
                            <td class="table-cell-sm">
                                <p class="text-gray-900 dark:text-white">
                                    {{ row.producer.name || "—" }}
                                </p>
                                <p
                                    v-if="row.producer.deleted"
                                    class="text-xs text-red-600 dark:text-red-400"
                                >
                                    Produtor excluído
                                </p>
                            </td>
                            <td class="table-cell-sm">
                                <span
                                    v-if="row.deleted"
                                    class="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200"
                                >
                                    Excluída
                                </span>
                                <span
                                    v-else-if="!row.is_active"
                                    class="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                                >
                                    Inativa
                                </span>
                                <span
                                    v-else
                                    class="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
                                >
                                    Ativa
                                </span>
                            </td>
                        </template>
                        <td class="table-cell-sm">
                            <span
                                class="rounded px-2 py-0.5 text-xs font-medium"
                                :class="
                                    row.value === 'v2'
                                        ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                "
                            >
                                {{ experienceLabel(row.value) }}
                            </span>
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ row.updated_by || "—" }}
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ row.updated_at || "—" }}
                        </td>
                        <td class="table-cell-sm text-gray-900 dark:text-white">
                            {{ row.reason || "—" }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="pagination.last_page > 1" class="my-4 flex flex-col items-center">
                <span class="mb-1 text-xs text-gray-500 dark:text-gray-400">
                    Exibindo {{ pagination.from }} a {{ pagination.to }} registros de
                    {{ pagination.total }}
                </span>
                <pagination :pagination="pagination" :offset="1" @paginate="fetch" />
            </div>
        </div>
    </div>
</template>

<script>
import SingleSelect from "../../forms/SingleSelect.vue";
import TextInput from "../../forms/TextInput.vue";
import EmptyState from "../../layouts/EmptyState.vue";
import Pagination from "../../layouts/Pagination.vue";

const EXPERIENCE_LABELS = { legacy: "Legado", v2: "Checkout 2.0" };

export default {
    name: "ExperienceRulesTable",

    components: { EmptyState, Pagination, SingleSelect, TextInput },

    props: {
        kind: {
            type: String,
            required: true,
            validator: (value) => ["producer", "offer"].includes(value),
        },
        refreshKey: { type: Number, default: 0 },
    },

    data() {
        return {
            loading: true,
            rows: [],
            filters: { value: null, search: "" },
            pagination: {
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
                total: 0,
                per_page: 15,
            },
            valueOptions: [
                { value: null, label: "Todos os valores" },
                { value: "legacy", label: "Legado" },
                { value: "v2", label: "Checkout 2.0" },
            ],
        };
    },

    computed: {
        isProducer() {
            return this.kind === "producer";
        },

        title() {
            return this.isProducer ? "Produtores marcados" : "Ofertas com exceção";
        },

        emptyTitle() {
            return this.isProducer ? "nenhum produtor marcado" : "nenhuma oferta com exceção";
        },

        emptyMessage() {
            return this.isProducer
                ? "Nenhum produtor tem a experiência do checkout marcada."
                : "Nenhuma oferta tem exceção de experiência do checkout.";
        },

        searchPlaceholder() {
            return this.isProducer ? "Nome ou e-mail do produtor" : "Código ou nome da oferta";
        },

        headers() {
            const tail = ["Valor", "Último autor", "Data", "Motivo"];

            return this.isProducer
                ? ["Produtor", "Estado", ...tail]
                : ["Oferta", "Produto", "Produtor", "Estado", ...tail];
        },
    },

    watch: {
        refreshKey() {
            this.fetch();
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        experienceLabel(value) {
            return EXPERIENCE_LABELS[value] || value;
        },

        apply() {
            this.pagination.current_page = 1;
            this.fetch();
        },

        fetch() {
            this.loading = true;

            const params = { page: this.pagination.current_page };

            if (this.filters.value) {
                params.value = this.filters.value;
            }

            if (this.filters.search.trim() !== "") {
                params.search = this.filters.search.trim();
            }

            axios
                .get("/api/checkout-experience/" + this.kind + "s", { params })
                .then(({ data }) => {
                    this.rows = data.data;
                    this.pagination = {
                        current_page: data.current_page,
                        last_page: data.last_page,
                        from: data.from,
                        to: data.to,
                        total: data.total,
                        per_page: data.per_page,
                    };
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text:
                                response?.data?.message ||
                                "Falha ao carregar a lista.",
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
